import React, { useEffect } from 'react';
import styled from 'styled-components/macro';
import { connect } from 'react-redux';
import {
  fetchCatchupDays,
  fetchCatchupDay,
  fetchExtraStreamsDay,
  catchUpFeature,
  resetProgAvail,
} from '../actions';

import ListItemHolderV2 from './ListItemHolderV2';

import translate from './TranslateHOC';
import {
  twoDigitNum,
  selectDay,
  selectMonth,
  getTheDate,
  getTodaysDate,
} from '../helpers';
import PageMeta from './PageMeta';
import { PAGE_TITLE_SEPARATOR } from '../constants';
import IsLoadingAnim from './alerts/IsLoadingAnim';
import { Tab, Tabs, TabList, TabPanel } from 'react-tabs';
import { device } from '../v2/Theme';
import { DayNav } from '../v2/components/tvSchedule/DayNav';
import TVHighlights from './TVHighlights';
import InvalidDate from '../v2/components/tvSchedule/InvalidDate';

function TVSchedule({
  strings,
  day,
  days,
  dayLoading,
  featureItems,
  featureErrored,
  curLang,
  match,
  fetchCatchupDays,
  fetchCatchupDay,
  fetchExtraStreamsDay,
  fetchFeature,
  extraStreamsDay,
}) {
  const currentPath = window.location.pathname;
  const validDate = days.some((e) => e.date_url === getTheDate(currentPath));
  const todaysDate = new Date().toISOString().substring(0, 10);
  const time = new Date().getHours();
  const theDate = getTheDate(currentPath);

  const indexDay = () => {
    if (days && days.length) {
      for (let i = 0; i < days.length; i++) {
        if (days[i].date_url === theDate) {
          return i;
        }
      }
      return null;
    }
  };
  const indexDayInt = indexDay();
  const firstSlide = indexDayInt !== null ? indexDayInt : 33;

  const pageTitle = (date) => {
    const today = getTodaysDate().string;

    if (date === today) {
      return strings.catchUp;
    }

    const itemDate = new Date(date);
    const itemDay = selectDay(curLang, itemDate.getDay());
    const itemMonth = selectMonth(curLang, itemDate.getMonth());

    return `${
      strings.catchUp
    } ${PAGE_TITLE_SEPARATOR} ${itemDay} ${itemDate.getDate()} ${itemMonth}`;
  };

  const highlightsTitle = (date) => {
    const yesterdaysDate = new Date(Date.now() - 24 * 60 * 60 * 1000);
    const yesterdaysDateString = `${yesterdaysDate.getFullYear()}-${twoDigitNum(
      yesterdaysDate.getMonth() + 1
    )}-${twoDigitNum(yesterdaysDate.getDate())}`;

    switch (date) {
      case getTodaysDate().string:
        return strings.todaysHighlights;
      case yesterdaysDateString:
        return strings.yesterdaysHighlights;
      default:
        const itemDate = new Date(date);
        const itemDay = selectDay(curLang, itemDate.getDay());
        const itemMonth = selectMonth(curLang, itemDate.getMonth());
        const prefix = curLang === 0 ? strings.highlights : '';

        return `${prefix} ${itemDay} ${itemDate.getDate()} ${itemMonth} ${
          strings.highlights
        }`;
    }
  };

  const calcTab = () => {
    if (time >= 6 && time <= 11) {
      return 0;
    } else if (time >= 12 && time <= 17) {
      return 1;
    } else if (time >= 18 && time <= 23) {
      return 2;
    } else {
      return 0;
    }
  };

  const formattedDate = getTheDate(currentPath).split('-').reverse().join('-');

  const getProgrammeLength = (day) => {
    const programmeParent = day.filter((day) => {
      return day.programmes[0]
        ? day.programmes[0] && day.time !== day.programmes[0].time
        : !day.programmes[0];
    });
    const totalProgrammes = day.reduce((count, currDay) => {
      return count + currDay.programmes.length;
    }, 0);
    return totalProgrammes + programmeParent.length;
  };

  useEffect(() => {
    fetchCatchupDays();
    fetchCatchupDay(getTheDate(currentPath));
    fetchFeature(getTheDate(currentPath));
    fetchExtraStreamsDay(getTheDate(currentPath)); // new feed
    resetProgAvail();
    window.scrollTo(0, 0);
  }, [
    curLang,
    currentPath,
    fetchCatchupDay,
    fetchCatchupDays,
    fetchFeature,
    fetchExtraStreamsDay,
  ]);

  if (!Array.isArray(extraStreamsDay)) {
    extraStreamsDay = []; // Initialize as an empty array if not an array
  }

  const filteredExtraStreamsDay = extraStreamsDay.filter((item) => {
    // remove programmes that aren't explicitly for this day
    const itemDate = item.live_date_time.split('T')[0];
    return itemDate === theDate;
  });

  return (
    <div className="tv-schedule main">
      <PageMeta
        title={pageTitle(theDate) ? pageTitle(theDate) : strings.tvSchedule}
        description={strings.metaDescriptionCatchUp}
      />

      <h2 className="page-title">{strings.tvSchedule}</h2>
      <DayNav
        firstSlide={firstSlide}
        days={days}
        theDate={theDate}
        match={match}
      />

      {featureItems.length > 0 && !featureErrored && (
        <section className="card-grid-container card-grid-container--no-line">
          <TVHighlights
            items={featureItems}
            gridType={`highlights highlights-${
              featureItems.length > 3 ? 3 : featureItems.length
            }`}
            checkToday={theDate === todaysDate}
            extraFeeds={filteredExtraStreamsDay}
            checkAvail={true}
          />
          <h2 className="page-title">
            {highlightsTitle(getTheDate(currentPath))}
          </h2>
        </section>
      )}

      {dayLoading && <IsLoadingAnim classNames="is-loading--large " />}

      <InvalidDate
        dayLoading={dayLoading}
        validDate={validDate}
        formattedDate={formattedDate}
        curLang={curLang}
        match={match}
        todaysDate={getTodaysDate()}
      />

      <div className="tv-schedule__listings">
        {!dayLoading && validDate && Object.keys(day).length > 0 && (
          <Tabs defaultIndex={theDate === todaysDate ? calcTab() : 0}>
            <TabList>
              {['morning', 'afternoon', 'evening'].map((timeOfDay) => (
                <Tab key={timeOfDay} tabIndex="0">
                  <TagContainer>
                    {strings[timeOfDay]}
                    <Tag>{getProgrammeLength(day[timeOfDay])}</Tag>
                  </TagContainer>
                </Tab>
              ))}
            </TabList>

            {['morning', 'afternoon', 'evening'].map((timeOfDay) => (
              <TabPanel key={timeOfDay}>
                {Object.keys(day[timeOfDay]).map((key) => (
                  <div key={key}>
                    <ListItemHolderV2
                      index={key}
                      curLang={curLang}
                      details={day[timeOfDay][key]}
                      isToday={theDate === todaysDate}
                    />
                  </div>
                ))}
              </TabPanel>
            ))}
          </Tabs>
        )}
      </div>
    </div>
  );
}

const TagContainer = styled.div`
  display: flex;
  flex-direction: row;
  align-items: center;
`;

const Tag = styled.span`
  background-color: ${(props) =>
    props.theme.name === 'light'
      ? props.theme.color.white90
      : props.theme.color.black90};
  color: ${(props) =>
    props.theme.name === 'light'
      ? props.theme.color.black90
      : props.theme.color.white90};
  border-radius: 20px;
  font-size: 12px;
  font-weight: 400;
  line-height: 16px;
  margin-left: 8px;
  padding: 4px 12px;
  text-transform: uppercase;
  @media ${device.tablet} {
    padding: 5px 13px;
    font-size: 14px;
    line-height: 18px;
  }
`;

const mapStateToProps = (state) => {
  return {
    curLang: state.curLang,
    days: state.catchupDays.success,
    day: state.catchupDay.success,
    dayErrored: state.catchupDay.error,
    dayLoading: state.catchupDay.loading,
    featureItems: state.catchUpFeatureItems.success,
    featureErrored: state.catchUpFeatureItems.error,
    featureLoading: state.catchUpFeatureItems.loading,
    extraStreamsDay: state.extraFeedDayItems.success,
    extraStreamsDayErrored: state.extraFeedDayItems.error,
    extraStreamsDayLoading: state.extraFeedDayItems.loading,
  };
};

const mapDispatchToProps = (dispatch) => {
  return {
    fetchCatchupDay: (date) => dispatch(fetchCatchupDay(date)),
    fetchCatchupDays: () => dispatch(fetchCatchupDays()),
    fetchFeature: (date) => dispatch(catchUpFeature(date)),
    resetProgAvail: () => dispatch(resetProgAvail()),
    fetchExtraStreamsDay: (date) => dispatch(fetchExtraStreamsDay(date)),
  };
};

export default translate(
  connect(mapStateToProps, mapDispatchToProps)(TVSchedule)
);
